import { useState, useCallback, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../../../lib/api";
import { PageHeader } from "../components/shared/PageHeader";
import { AdminTable } from "../components/shared/AdminTable";
import { SlideOver } from "../components/shared/SlideOver";
import { StatusBadge } from "../components/shared/StatusBadge";
import { ConfirmDialog } from "../components/shared/ConfirmDialog";
import { FormField } from "../components/shared/FormField";
import { Home, MapPin } from "lucide-react";
import { useForm, Controller } from "react-hook-form";
import { ImageUpload } from "../../../components/common/ImageUpload";
import { MultiImageUpload } from "../../../components/common/MultiImageUpload";
import { useCategories } from "../../../hooks/useCategories";
import { useAdminT } from "../../../hooks/useAdminT";
import { useFormValidation } from "../../../hooks/useFormValidation";
import { useSearchParams, useNavigate } from "react-router-dom";
import { RemoteImage } from "../../../core/components/RemoteImage";
import { PaymentPlansEditor } from "../components/shared/PaymentPlansEditor";
import { formatCurrency } from "../../../core/utils/formatters";
import toast from "react-hot-toast";

export default function UnitsPage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { t, lang } = useAdminT();
    const { handleServerErrors } = useFormValidation();
    const [isDrawOpen, setIsDrawOpen] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<any>(null);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(20);

    const { register, handleSubmit, reset, control, setError, formState: { errors } } = useForm<any>();

    // Handle deep link for creation
    useEffect(() => {
        if (searchParams.get('action') === 'create') {
            setIsDrawOpen(true);
            const pid = searchParams.get('projectId');
            if (pid) {
                // Initialize form with project ID
                reset({
                    projectId: pid,
                    slug: "",
                    type: "",
                    price: "",
                    status: "AVAILABLE",
                    published: false,
                    featured: false,
                    constructionStatus: "UNDER_CONSTRUCTION",
                    gallery: [],
                    latitude: null,
                    longitude: null,
                    boundary: []
                });
            }
        }
    }, [searchParams, reset]);

    // List endpoint returns FORMATTED data: { id, slug, title, price, image, type, beds, baths, area, project: { title }, published, featured }
    const { data: unitData, isLoading } = useQuery({
        queryKey: ["units", { page, pageSize }],
        queryFn: () => api.get(`/api/admin/units?page=${page + 1}&limit=${pageSize}`).then(res => res.data)
    });

    const units = unitData?.data || [];
    const pagination = unitData?.pagination || { total: 0, page: 1, limit: 20, pages: 0 };

    const { data: amenities } = useQuery({
        queryKey: ["amenities"],
        queryFn: () => api.get("/api/admin/amenities").then(res => res.data.data)
    });

    const { data: projects } = useQuery({
        queryKey: ["projects"],
        queryFn: () => api.get("/api/admin/projects").then(res => res.data.data)
    });

    const { categories } = useCategories();

    // Helper to get translated name — uses the active UI language
    const getTransName = (item: any) =>
        item?.translations?.find((t: any) => t.locale === lang)?.name
        || item?.translations?.find((t: any) => t.locale === "en")?.name
        || item?.slug || "-";

    const createMutation = useMutation({
        mutationFn: (data: any) => api.post("/api/admin/units", data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["units"] });
            toast.success("Unit saved successfully");
            handleClose();
        },
        onError: (error: any) => handleServerErrors(error, setError, "Failed to save unit"),
    });

    const updateMutation = useMutation({
        mutationFn: (data: any) => api.put(`/api/admin/units/${editingId}`, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["units"] });
            toast.success("Unit updated successfully");
            handleClose();
        },
        onError: (error: any) => handleServerErrors(error, setError, "Failed to update unit"),
    });

    const deleteMutation = useMutation({
        mutationFn: (id: string) => api.delete(`/api/admin/units/${id}`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["units"] });
            toast.success("Unit deleted");
            setDeleteTarget(null);
        },
        onError: () => {
            toast.error("Failed to delete unit");
            setDeleteTarget(null);
        }
    });

    const handleClose = () => {
        setIsDrawOpen(false);
        setEditingId(null);
        reset();
    };

    // Fetch by ID to get RAW data with translations[] for editing
    const handleEdit = useCallback(async (item: any) => {
        console.log("UnitsPage: handleEdit called for", item.id);
        try {
            const res = await api.get(`/api/admin/units/${item.id}`);
            console.log("UnitsPage: fetch success", res.data);
            const unit = res.data.data;
            const enTrans = unit.translations?.find((t: any) => t.locale === "en") || {};
            const arTrans = unit.translations?.find((t: any) => t.locale === "ar") || {};

            setEditingId(unit.id);
            reset({
                slug: unit.slug || "",
                projectId: unit.projectId,
                categoryId: unit.categoryId,
                price: unit.price || "",
                beds: unit.beds ?? "",
                baths: unit.baths ?? "",
                area: unit.area ?? "",
                payment: unit.payment || "",
                paymentDuration: unit.paymentDuration || "",
                published: unit.published ?? false,
                featured: unit.featured ?? false,
                constructionStatus: unit.constructionStatus || "UNDER_CONSTRUCTION",
                status: unit.status || "AVAILABLE",
                image: unit.image || "",
                blueprint: unit.blueprint || "",
                titleEn: enTrans.title || "",
                titleAr: arTrans.title || "",
                typeTextEn: enTrans.typeText || "",
                typeTextAr: arTrans.typeText || "",
                locationEn: enTrans.location || "",
                locationAr: arTrans.location || "",
                gallery: unit.gallery || [],
                amenityIds: unit.amenities?.map((a: any) => a.amenity.id) || [],
                finishType: unit.finishType || "",
                deliveryDate: unit.deliveryDate || "",
                finishTextEn: enTrans.finishText || "",
                finishTextAr: arTrans.finishText || "",
                paymentPlans: unit.paymentPlans || [],
            });



            setIsDrawOpen(true);
        } catch (err) {
            console.error("Failed to fetch unit for editing:", err);
            alert("Failed to load unit details. Check console for error.");
        }
    }, [reset]);

    const onSubmit = (data: any) => {
        const payload: any = {
            projectId: data.projectId,
            categoryId: data.categoryId,
            price: data.price,
            beds: data.beds ? Number(data.beds) : null,
            baths: data.baths ? Number(data.baths) : null,
            area: data.area ? Number(data.area) : null,
            payment: data.payment || null,
            paymentDuration: data.paymentDuration || null,
            published: Boolean(data.published),
            featured: Boolean(data.featured),
            constructionStatus: data.constructionStatus || "UNDER_CONSTRUCTION",
            status: data.status || "AVAILABLE",
            image: data.image || null,
            blueprint: data.blueprint || null,
            gallery: data.gallery || [],
            amenityIds: data.amenityIds || [],
            finishType: data.finishType || null,
            deliveryDate: data.deliveryDate || null,
            paymentPlans: data.paymentPlans || [],
            translations: [
                { locale: "en", title: data.titleEn, typeText: data.typeTextEn || null, location: data.locationEn || null, finishText: data.finishTextEn || null },
                { locale: "ar", title: data.titleAr, typeText: data.typeTextAr || null, location: data.locationAr || null, finishText: data.finishTextAr || null },
            ],
        };

        if (!editingId) {
            payload.slug = data.slug;
        }

        if (editingId) {
            updateMutation.mutate(payload);
        } else {
            createMutation.mutate(payload);
        }
    };

    return (
        <div className="p-8">
            <PageHeader
                title={t("units.title")}
                description={t("units.subtitle")}
                actions={[
                    {
                        label: t("units.add"),
                        icon: Home,
                        onClick: () => setIsDrawOpen(true)
                    }
                ]}
            />

            <AdminTable
                data={units}
                isLoading={isLoading}
                serverSide
                total={pagination.total}
                pageIndex={page}
                pageSize={pageSize}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
                columns={[
                    {
                        header: t("common.image"),
                        cell: (u) => u.image
                            ? <RemoteImage src={u.image} className="w-12 h-12 object-cover rounded" />
                            : <div className="w-12 h-12 bg-slate-100 rounded" />,
                        enableSorting: false,
                    },
                    { header: t("common.title"), cell: (u) => u.title || u.slug || "-" },
                    { header: t("nav.projects"), cell: (u) => u.project?.title || "-" },
                    { header: t("nav.categories"), cell: (u) => u.category?.name?.en || u.category?.slug || "-" },
                    { header: t("units.fields.price"), cell: (u) => formatCurrency(u.price) || "-" },
                    { header: t("units.fields.beds"), cell: (u) => `${u.beds ?? 0} / ${u.baths ?? 0}` },
                    { header: t("common.published"), cell: (u) => <StatusBadge status={u.published} /> },
                ]}
                onEdit={handleEdit}
                onDelete={(u) => setDeleteTarget(u)}
                actions={(u) => (
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            navigate(`/admin/units/${u.id}/map`);
                        }}
                        className="p-2 text-slate-400 hover:text-secondary hover:bg-secondary/10 rounded-md transition-colors"
                        title={t("projects.fields.mapLocation" as any) || "Location"}
                    >
                        <MapPin className="w-4 h-4" />
                    </button>
                )}
            />

            <ConfirmDialog
                isOpen={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
                title={t("units.delete")}
                message={`${t("common.confirmProceed")} "${deleteTarget?.title || t("units.title")}"?`}
                confirmText={t("common.delete")}
                isLoading={deleteMutation.isPending}
            />

            <SlideOver
                isOpen={isDrawOpen}
                onClose={handleClose}
                title={editingId ? t("units.edit") : t("units.add")}
                footer={
                    <>
                        <button onClick={handleClose} className="px-4 py-2 border rounded hover:bg-slate-50">{t("common.cancel")}</button>
                        <button onClick={handleSubmit(onSubmit)} className="px-4 py-2 bg-secondary text-white rounded hover:bg-primary-dark">
                            {editingId ? t("common.save") : t("common.create")} {t("units.title")}
                        </button>
                    </>
                }
            >
                <form className="space-y-6">
                    {!editingId && (
                        <FormField label={t("common.slug")} error={errors.slug?.message as string} required hint="Only lowercase letters, numbers, and hyphens">
                            <input {...register("slug", {
                                required: "Slug is required",
                                pattern: { value: /^[a-z0-9-]+$/, message: "Only lowercase letters, numbers, and hyphens" }
                            })} className="w-full px-3 py-2 border rounded-md" placeholder="unit-slug" />
                        </FormField>
                    )}

                    <FormField label={t("nav.projects")} error={errors.projectId?.message as string} required>
                        <select {...register("projectId", { required: "Project is required" })} className="w-full px-3 py-2 border rounded-md">
                            <option value="">{t("nav.projects")}</option>
                            {projects?.map((p: any) => <option key={p.id} value={p.id}>{p.title || p.slug}</option>)}
                        </select>
                    </FormField>

                    <div className="grid grid-cols-2 gap-4">
                        <FormField label={t("nav.categories")} error={errors.categoryId?.message as string} required>
                            <select {...register("categoryId", { required: "Category is required" })} className="w-full px-3 py-2 border rounded-md">
                                <option value="">{t("nav.categories")}</option>
                                {categories?.map((c: any) => (
                                    <option key={c.id} value={c.id}>{getTransName(c)}</option>
                                ))}
                            </select>
                        </FormField>
                        <FormField label={t("units.fields.price")} error={errors.price?.message as string} required>
                            <input type="number" step="any" min="0" {...register("price", { required: true, valueAsNumber: true })} className="w-full px-3 py-2 border rounded-md" placeholder="e.g. 1500000" />
                        </FormField>
                    </div>

                    <div className="col-span-2">
                        <label className="block text-sm font-medium mb-1">{t("nav.amenities")}</label>
                        <div className="grid grid-cols-2 gap-2 border rounded-md p-3 max-h-40 overflow-y-auto">
                            {amenities?.map((a: any) => (
                                <label key={a.id} className="flex items-center gap-2 cursor-pointer hover:bg-slate-50 p-1 rounded">
                                    <input
                                        type="checkbox"
                                        value={a.id}
                                        {...register("amenityIds")}
                                        className="h-4 w-4 rounded border-slate-300 text-secondary focus:ring-secondary"
                                    />
                                    <span className="text-sm">{getTransName(a)}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-medium">{t("units.fields.beds")}</label>
                            <input type="number" {...register("beds")} className="mt-1 w-full px-3 py-2 border rounded-md" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium">{t("units.fields.baths")}</label>
                            <input type="number" {...register("baths")} className="mt-1 w-full px-3 py-2 border rounded-md" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium">{t("units.fields.area")}</label>
                            <input type="number" step="any" {...register("area", { valueAsNumber: true })} className="mt-1 w-full px-3 py-2 border rounded-md" />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium">{t("units.fields.payment")}</label>
                            <input {...register("payment")} className="mt-1 w-full px-3 py-2 border rounded-md" placeholder="e.g. 10%" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium">{t("units.fields.paymentDuration")}</label>
                            <input {...register("paymentDuration")} className="mt-1 w-full px-3 py-2 border rounded-md" placeholder="e.g. 8 years" />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium">{t("units.fields.finishType")}</label>
                            <select {...register("finishType")} className="mt-1 w-full px-3 py-2 border rounded-md">
                                <option value="">{t("units.fields.finishType")}</option>
                                <option value="core_shell">{t("units.finishTypes.core_shell")}</option>
                                <option value="semi_finished">{t("units.finishTypes.semi_finished")}</option>
                                <option value="finished">{t("units.finishTypes.finished")}</option>
                                <option value="luxury">{t("units.finishTypes.luxury")}</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium">{t("units.fields.deliveryDate")}</label>
                            <select {...register("deliveryDate")} className="mt-1 w-full px-3 py-2 border rounded-md">
                                <option value="">{t("units.fields.deliveryDate")}</option>
                                {Array.from({ length: 15 }, (_, i) => new Date().getFullYear() - 2 + i).map(year => (
                                    <option key={year} value={year}>{year}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* English Translations */}
                    <div className="border border-slate-200 rounded-lg p-4 bg-slate-50">
                        <h3 className="font-medium text-slate-800 mb-3">{t("common.locale.en")}</h3>
                        <div className="space-y-3">
                            <FormField label={t("common.title")} error={errors.titleEn?.message as string} required>
                                <input {...register("titleEn", { required: "English title is required" })} className="w-full px-3 py-2 border rounded-md bg-white" />
                            </FormField>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <label className="block text-xs font-medium uppercase text-slate-500">{t("units.fields.typeText")}</label>
                                <input {...register("typeTextEn")} className="mt-1 w-full px-3 py-2 border rounded-md bg-white text-sm" placeholder="e.g. Luxury Villa" />
                            </div>
                            <div>
                                <label className="block text-xs font-medium uppercase text-slate-500">{t("common.location")}</label>
                                <input {...register("locationEn")} className="mt-1 w-full px-3 py-2 border rounded-md bg-white text-sm" placeholder="e.g. New Cairo" />
                            </div>
                        </div>
                        <div>
                            <label className="block text-xs font-medium uppercase text-slate-500">{t("units.fields.finishText")}</label>
                            <input {...register("finishTextEn")} className="mt-1 w-full px-3 py-2 border rounded-md bg-white text-sm" placeholder="e.g. Fully Finished with ACs" />
                        </div>
                    </div>


                    {/* Arabic Translations */}
                    <div className="border border-slate-200 rounded-lg p-4 bg-slate-50" dir="rtl">
                        <h3 className="font-medium text-slate-800 mb-3">المحتوى العربي</h3>
                        <div className="space-y-3">
                            <FormField label="العنوان" error={errors.titleAr?.message as string} required>
                                <input {...register("titleAr", { required: "Arabic title is required" })} className="w-full px-3 py-2 border rounded-md bg-white" dir="rtl" />
                            </FormField>
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <label className="block text-xs font-medium uppercase text-slate-500">نوع الوحدة</label>
                                    <input {...register("typeTextAr")} className="mt-1 w-full px-3 py-2 border rounded-md bg-white text-sm" />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium uppercase text-slate-500">الموقع</label>
                                    <input {...register("locationAr")} className="mt-1 w-full px-3 py-2 border rounded-md bg-white text-sm" />
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-medium uppercase text-slate-500">نص التشطيب (اختياري)</label>
                                <input {...register("finishTextAr")} className="mt-1 w-full px-3 py-2 border rounded-md bg-white text-sm" />
                            </div>
                        </div>
                    </div>

                    {/* Payment Plans Array Editor */}
                    <div className="border border-slate-200 rounded-lg p-4 bg-white">
                        <PaymentPlansEditor control={control} register={register} name="paymentPlans" />
                    </div>

                    {/* Image — required */}
                    <FormField label={t("common.image")} error={errors.image?.message as string} required>
                        <Controller
                            name="image"
                            control={control}
                            rules={{ validate: (v) => !!v || "Unit image is required" }}
                            render={({ field }) => (
                                <ImageUpload value={field.value ?? ""} onChange={field.onChange} />
                            )}
                        />
                    </FormField>

                    {/* Blueprint */}
                    <div>
                        <label className="block text-sm font-medium mb-1">{t("units.fields.blueprint")}</label>
                        <Controller
                            name="blueprint"
                            control={control}
                            render={({ field }) => (
                                <ImageUpload value={field.value} onChange={field.onChange} />
                            )}
                        />
                    </div>

                    {/* Gallery */}
                    <div>
                        <Controller
                            name="gallery"
                            control={control}
                            render={({ field }) => (
                                <MultiImageUpload
                                    value={(field.value as string[]) || []}
                                    onChange={field.onChange}
                                    label={t("common.gallery")}
                                />
                            )}
                        />
                    </div>


                    {/* Options */}
                    <div className="flex gap-6">
                        <label className="flex items-center gap-2">
                            <input type="checkbox" {...register("published")} className="h-4 w-4 rounded" />
                            <span className="text-sm font-medium">{t("common.published")}</span>
                        </label>
                        <label className="flex items-center gap-2">
                            <input type="checkbox" {...register("featured")} className="h-4 w-4 rounded" />
                            <span className="text-sm font-medium">{t("common.featured")}</span>
                        </label>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium">{t("units.fields.constructionStatus")}</label>
                            <select {...register("constructionStatus")} className="mt-1 w-full px-3 py-2 border rounded-md">
                                <option value="UNDER_CONSTRUCTION">{t("status.underConstruction")}</option>
                                <option value="READY_TO_MOVE">{t("status.readyToMove")}</option>
                                <option value="DELIVERED">{t("status.delivered")}</option>
                                <option value="OFF_PLAN">{t("status.offPlan")}</option>
                            </select>
                        </div>

                        {/* Status */}
                        <div>
                            <label className="block text-sm font-medium">{t("units.fields.status")}</label>
                            <select {...register("status")} className="mt-1 w-full px-3 py-2 border rounded-md">
                                <option value="AVAILABLE">{t("status.available")}</option>
                                <option value="SOLD">{t("status.sold")}</option>
                                <option value="RESALE">{t("status.resale")}</option>
                            </select>
                        </div>
                    </div>
                </form >
            </SlideOver >
        </div >
    );
}
